// components/AssistantModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { X, Calendar, Lightbulb, Users, Phone, Gift, TrendingUp, Clock, Star, CheckCircle, Target, MessageCircle, ListChecks } from 'lucide-react';
// unused imports removed
import { getAllCustomerHistory } from '../hooks/useCustomerHistory';

// 날짜 유틸
const daysBetween = (a, b) => {
    const ms = b.getTime() - a.getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
};

const getLastHistoryDate = (customerId) => {
    try {
        const history = getAllCustomerHistory(customerId) || [];
        if (!Array.isArray(history) || history.length === 0) return null;
        const latest = history
            .filter(h => !!h.timestamp)
            .map(h => new Date(h.timestamp))
            .sort((a, b) => b.getTime() - a.getTime())[0];
        return latest || null;
    } catch (_) {
        return null;
    }
};

const AssistantModal = ({ customers, onClose, onSelectCustomer }) => {
    const [activeTab, setActiveTab] = useState('birthday');
    const [birthdayCustomers, setBirthdayCustomers] = useState([]);
    const [todayTodoCustomers, setTodayTodoCustomers] = useState([]);
    const [followUpMap, setFollowUpMap] = useState({}); // { [customerId]: Array<{ content, date }> }
    const [upcomingTodoCustomers, setUpcomingTodoCustomers] = useState([]);
    const [upcomingFollowUpMap, setUpcomingFollowUpMap] = useState({}); // { [customerId]: Array<{ content, date, daysLeft }> }
    const [insights, setInsights] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null); // 선택된 날짜 (YYYY-MM-DD)
    const [showDateModal, setShowDateModal] = useState(false); // 날짜별 할일 모달 표시 여부
    const [dateTodos, setDateTodos] = useState([]); // 선택된 날짜의 할일 목록
    const [loadingDateTodos, setLoadingDateTodos] = useState(false); // 날짜별 할일 로딩 상태
    const [todosByDate, setTodosByDate] = useState({}); // 날짜별 할일 개수

    // functions hoisted for useEffect deps

    // 초기 탭: URL 쿼리 파라미터(tab) 반영
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const tab = params.get('tab');
            if (tab && (tab === 'birthday' || tab === 'insights' || tab === 'todo')) {
                setActiveTab(tab);
            }
        } catch (_) { }
    }, []);

    // 오늘 생일인 고객 찾기
    const loadBirthdayCustomers = useCallback(async () => {
        try {
            // 페이지네이션과 관계없이 IndexedDB에서 직접 모든 고객 조회
            const { idbGetAllCustomers } = await import('../utils/idb');
            const allCustomers = await idbGetAllCustomers();

            const today = new Date();
            const todayMonth = today.getMonth() + 1; // 0-based to 1-based
            const todayDay = today.getDate();

            console.log('[AI비서 모달] 디버깅 시작 - 오늘 날짜:', todayMonth + '/' + todayDay);
            console.log('[AI비서 모달] 전체 고객 수:', allCustomers.length);
            
            // 생일이 있는 고객들만 필터링해서 로그 출력
            const customersWithBirthdate = allCustomers.filter(c => c?.birthdate);
            console.log('[AI비서 모달] 생일 정보가 있는 고객 수:', customersWithBirthdate.length);
            if (customersWithBirthdate.length > 0) {
                console.log('[AI비서 모달] 생일 정보 샘플 (최대 5개):', 
                    customersWithBirthdate.slice(0, 5).map(c => ({
                        name: c.name,
                        birthdate: c.birthdate,
                        birthdateType: typeof c.birthdate
                    }))
                );
            }

            // 생일 날짜 비교 함수 (시간대 문제 방지)
            const isBirthdayToday = (birthdateValue) => {
                if (!birthdateValue) return false;
                
                let month, day;
                
                // 문자열 형식인 경우
                if (typeof birthdateValue === 'string') {
                    // 앞뒤 공백 제거
                    const trimmed = birthdateValue.trim();
                    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (match) {
                        month = parseInt(match[2], 10);
                        day = parseInt(match[3], 10);
                    } else {
                        // 다른 형식의 문자열인 경우 Date 객체로 파싱 시도
                        const birth = new Date(trimmed);
                        if (isNaN(birth.getTime())) return false;
                        month = birth.getMonth() + 1;
                        day = birth.getDate();
                    }
                } else {
                    // Date 객체나 타임스탬프인 경우
                    const birth = new Date(birthdateValue);
                    if (isNaN(birth.getTime())) return false;
                    month = birth.getMonth() + 1;
                    day = birth.getDate();
                }
                
                const isMatch = month === todayMonth && day === todayDay;
                if (isMatch) {
                    console.log('[AI비서 모달] 생일 매칭 성공:', { birthdateValue, month, day, todayMonth, todayDay });
                }
                return isMatch;
            };

            const birthdayList = allCustomers.filter(customer => {
                if (!customer.birthdate) return false;
                return isBirthdayToday(customer.birthdate);
            });

            console.log('[AI비서 모달] 최종 생일 고객 수:', birthdayList.length);
            if (birthdayList.length > 0) {
                console.log('[AI비서 모달] 생일 고객 목록:', birthdayList.map(c => ({ name: c.name, birthdate: c.birthdate })));
            }

            setBirthdayCustomers(birthdayList);
        } catch (error) {
            console.error('생일 고객 로드 실패:', error);
            // 폴백: 메모리의 customers 사용
            const today = new Date();
            const todayMonth = today.getMonth() + 1;
            const todayDay = today.getDate();

            // 생일 날짜 비교 함수 (시간대 문제 방지)
            const isBirthdayToday = (birthdateValue) => {
                if (!birthdateValue) return false;
                
                let month, day;
                
                // 문자열 형식인 경우
                if (typeof birthdateValue === 'string') {
                    // 앞뒤 공백 제거
                    const trimmed = birthdateValue.trim();
                    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (match) {
                        month = parseInt(match[2], 10);
                        day = parseInt(match[3], 10);
                    } else {
                        // 다른 형식의 문자열인 경우 Date 객체로 파싱 시도
                        const birth = new Date(trimmed);
                        if (isNaN(birth.getTime())) return false;
                        month = birth.getMonth() + 1;
                        day = birth.getDate();
                    }
                } else {
                    // Date 객체나 타임스탬프인 경우
                    const birth = new Date(birthdateValue);
                    if (isNaN(birth.getTime())) return false;
                    month = birth.getMonth() + 1;
                    day = birth.getDate();
                }
                
                return month === todayMonth && day === todayDay;
            };

            const allCustomers = Object.values(customers).flat();
            const birthdayList = allCustomers.filter(customer => {
                if (!customer.birthdate) return false;
                return isBirthdayToday(customer.birthdate);
            });

            setBirthdayCustomers(birthdayList);
        }
    }, [customers]);

    // 날짜 유틸 (moved outside or memoized if needed, but here simple consts inside component are fine if deps are managed)
    // Actually, daysBetween and getLastHistoryDate are better as pure functions outside component or useCallback.
    // Since they are used in multiple places, I'll keep them but make them part of component scope or useCallback if they used props.
    // They don't use props. So I can move them outside component to avoid dep issues.

    // 오늘의 할일: followUpDate가 오늘(YYYY-MM-DD)인 고객
    const loadTodayTodos = useCallback(async () => {
        try {
            // 페이지네이션과 관계없이 IndexedDB에서 직접 모든 고객 조회
            const { idbGetAllCustomers } = await import('../utils/idb');
            const allCustomers = await idbGetAllCustomers();

            const now = new Date();
            const yyyy = String(now.getFullYear());
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const dd = String(now.getDate()).padStart(2, '0');
            const todayStr = `${yyyy}-${mm}-${dd}`;

            // 고객 필드 + 고객 히스토리의 Follow Up 병합
            const fromHistories = [];
            try {
                const results = await Promise.all(
                    allCustomers.map(async (c) => {
                        try {
                            const hist = await getAllCustomerHistory(c.id);
                            if (!Array.isArray(hist)) return null;
                            const hasToday = hist.some(h => {
                                const f = h?.followUp;
                                if (!f) return false;
                                // followUp가 객체 { content, date } 형태인 경우 우선 처리
                                if (typeof f === 'object' && f?.date) {
                                    if (typeof f.date === 'string') return f.date.startsWith(todayStr);
                                    if (f.date instanceof Date) {
                                        const fy = String(f.date.getFullYear());
                                        const fm = String(f.date.getMonth() + 1).padStart(2, '0');
                                        const fd = String(f.date.getDate()).padStart(2, '0');
                                        return `${fy}-${fm}-${fd}` === todayStr;
                                    }
                                    return false;
                                }
                                // 레거시: followUp가 문자열/Date였던 경우를 대비
                                if (typeof f === 'string') return f.startsWith(todayStr);
                                if (f instanceof Date) {
                                    const fy = String(f.getFullYear());
                                    const fm = String(f.getMonth() + 1).padStart(2, '0');
                                    const fd = String(f.getDate()).padStart(2, '0');
                                    return `${fy}-${fm}-${fd}` === todayStr;
                                }
                                return false;
                            });
                            return hasToday ? c : null;
                        } catch (_) {
                            return null;
                        }
                    })
                );
                for (const r of results) if (r) fromHistories.push(r);
            } catch (_) { }

            // 고객 필드에 설정된 Follow Up
            const fromCustomerField = allCustomers.filter(c => {
                const val = c.followUpDate || c.followUpPlanDate || c.followUp || null;
                if (!val) return false;
                if (typeof val === 'string') return val.startsWith(todayStr);
                if (val instanceof Date) {
                    const vyyyy = String(val.getFullYear());
                    const vmm = String(val.getMonth() + 1).padStart(2, '0');
                    const vdd = String(val.getDate()).padStart(2, '0');
                    return `${vyyyy}-${vmm}-${vdd}` === todayStr;
                }
                return false;
            });

            // 합치고 중복 제거
            const map = new Map();
            [...fromCustomerField, ...fromHistories].forEach(c => {
                if (c && c.id) map.set(c.id, c);
            });
            const merged = Array.from(map.values());

            // 가독성 향상을 위해 이름순 정렬
            merged.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setTodayTodoCustomers(merged);

            // 각 고객의 오늘자 FollowUp 상세(메모/날짜) 수집 - 여러 개 전부 수집
            try {
                const details = {};
                await Promise.all(
                    merged.map(async (c) => {
                        try {
                            const hist = await getAllCustomerHistory(c.id);
                            if (!Array.isArray(hist)) return;
                            const today = new Date();
                            const yyyy = String(today.getFullYear());
                            const mm = String(today.getMonth() + 1).padStart(2, '0');
                            const dd = String(today.getDate()).padStart(2, '0');
                            const todayStrLocal = `${yyyy}-${mm}-${dd}`;
                            const matchedList = hist.filter(h => {
                                const f = h?.followUp;
                                if (!f) return false;
                                if (typeof f === 'object' && f?.date) {
                                    return typeof f.date === 'string' ? f.date.startsWith(todayStrLocal) : false;
                                }
                                if (typeof f === 'string') return f.startsWith(todayStrLocal);
                                return false;
                            }).map(h => {
                                const f = h.followUp;
                                if (typeof f === 'object') {
                                    return { content: f.content || '', date: (typeof f.date === 'string' ? f.date.slice(0, 10) : todayStrLocal) };
                                }
                                return { content: '', date: (typeof f === 'string' ? f.slice(0, 10) : todayStrLocal) };
                            });
                            if (matchedList.length > 0) details[c.id] = matchedList;
                        } catch (_) { }
                    })
                );
                setFollowUpMap(details);
            } catch (_) { setFollowUpMap({}); }
        } catch (error) {
            console.error('할일 로드 실패:', error);
            // 폴백: 메모리의 customers 사용
            const allCustomers = Object.values(customers).flat();
            const now = new Date();
            const yyyy = String(now.getFullYear());
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const dd = String(now.getDate()).padStart(2, '0');
            const todayStr = `${yyyy}-${mm}-${dd}`;

            // 고객 필드 + 고객 히스토리의 Follow Up 병합
            const fromHistories = [];
            try {
                const results = await Promise.all(
                    allCustomers.map(async (c) => {
                        try {
                            const hist = await getAllCustomerHistory(c.id);
                            if (!Array.isArray(hist)) return null;
                            const hasToday = hist.some(h => {
                                const f = h?.followUp;
                                if (!f) return false;
                                // followUp가 객체 { content, date } 형태인 경우 우선 처리
                                if (typeof f === 'object' && f?.date) {
                                    if (typeof f.date === 'string') return f.date.startsWith(todayStr);
                                    if (f.date instanceof Date) {
                                        const fy = String(f.date.getFullYear());
                                        const fm = String(f.date.getMonth() + 1).padStart(2, '0');
                                        const fd = String(f.date.getDate()).padStart(2, '0');
                                        return `${fy}-${fm}-${fd}` === todayStr;
                                    }
                                    return false;
                                }
                                // 레거시: followUp가 문자열/Date였던 경우를 대비
                                if (typeof f === 'string') return f.startsWith(todayStr);
                                if (f instanceof Date) {
                                    const fy = String(f.getFullYear());
                                    const fm = String(f.getMonth() + 1).padStart(2, '0');
                                    const fd = String(f.getDate()).padStart(2, '0');
                                    return `${fy}-${fm}-${fd}` === todayStr;
                                }
                                return false;
                            });
                            return hasToday ? c : null;
                        } catch (_) {
                            return null;
                        }
                    })
                );
                for (const r of results) if (r) fromHistories.push(r);
            } catch (_) { }

            // 고객 필드에 설정된 Follow Up
            const fromCustomerField = allCustomers.filter(c => {
                const val = c.followUpDate || c.followUpPlanDate || c.followUp || null;
                if (!val) return false;
                if (typeof val === 'string') return val.startsWith(todayStr);
                if (val instanceof Date) {
                    const vyyyy = String(val.getFullYear());
                    const vmm = String(val.getMonth() + 1).padStart(2, '0');
                    const vdd = String(val.getDate()).padStart(2, '0');
                    return `${vyyyy}-${vmm}-${vdd}` === todayStr;
                }
                return false;
            });

            // 합치고 중복 제거
            const map = new Map();
            [...fromCustomerField, ...fromHistories].forEach(c => {
                if (c && c.id) map.set(c.id, c);
            });
            const merged = Array.from(map.values());

            // 가독성 향상을 위해 이름순 정렬
            merged.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setTodayTodoCustomers(merged);

            // 각 고객의 오늘자 FollowUp 상세(메모/날짜) 수집 - 여러 개 전부 수집
            try {
                const details = {};
                await Promise.all(
                    merged.map(async (c) => {
                        try {
                            const hist = await getAllCustomerHistory(c.id);
                            if (!Array.isArray(hist)) return;
                            const today = new Date();
                            const yyyy = String(today.getFullYear());
                            const mm = String(today.getMonth() + 1).padStart(2, '0');
                            const dd = String(today.getDate()).padStart(2, '0');
                            const todayStrLocal = `${yyyy}-${mm}-${dd}`;
                            const matchedList = hist.filter(h => {
                                const f = h?.followUp;
                                if (!f) return false;
                                if (typeof f === 'object' && f?.date) {
                                    return typeof f.date === 'string' ? f.date.startsWith(todayStrLocal) : false;
                                }
                                if (typeof f === 'string') return f.startsWith(todayStrLocal);
                                return false;
                            }).map(h => {
                                const f = h.followUp;
                                if (typeof f === 'object') {
                                    return { content: f.content || '', date: (typeof f.date === 'string' ? f.date.slice(0, 10) : todayStrLocal) };
                                }
                                return { content: '', date: (typeof f === 'string' ? f.slice(0, 10) : todayStrLocal) };
                            });
                            if (matchedList.length > 0) details[c.id] = matchedList;
                        } catch (_) { }
                    })
                );
                setFollowUpMap(details);
            } catch (_) { setFollowUpMap({}); }
        }
    }, [customers]);

    // 다가오는 할일: 오늘 이후 7일 이내의 Follow Up 날짜를 가진 고객
    const loadUpcomingTodos = useCallback(async () => {
        try {
            // 페이지네이션과 관계없이 IndexedDB에서 직접 모든 고객 조회
            const { idbGetAllCustomers } = await import('../utils/idb');
            const allCustomers = await idbGetAllCustomers();

            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const sevenDaysLater = new Date(today);
            sevenDaysLater.setDate(sevenDaysLater.getDate() + 7);

            // 날짜 범위 내의 모든 날짜 문자열 생성 (오늘+1일 ~ 오늘+7일)
            const dateStrings = [];
            for (let i = 1; i <= 7; i++) {
                const date = new Date(today);
                date.setDate(date.getDate() + i);
                const yyyy = String(date.getFullYear());
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                dateStrings.push(`${yyyy}-${mm}-${dd}`);
            }

            // 날짜 문자열이 범위 내인지 확인하는 헬퍼 함수
            const isInRange = (dateStr) => {
                if (!dateStr) return false;
                const dateOnly = typeof dateStr === 'string' ? dateStr.slice(0, 10) : dateStr;
                return dateStrings.includes(dateOnly);
            };

            // 고객 필드 + 고객 히스토리의 Follow Up 병합
            const fromHistories = [];
            try {
                const results = await Promise.all(
                    allCustomers.map(async (c) => {
                        try {
                            const hist = await getAllCustomerHistory(c.id);
                            if (!Array.isArray(hist)) return null;
                            const hasUpcoming = hist.some(h => {
                                const f = h?.followUp;
                                if (!f) return false;
                                // followUp가 객체 { content, date } 형태인 경우 우선 처리
                                if (typeof f === 'object' && f?.date) {
                                    if (typeof f.date === 'string') return isInRange(f.date);
                                    if (f.date instanceof Date) {
                                        const fy = String(f.date.getFullYear());
                                        const fm = String(f.date.getMonth() + 1).padStart(2, '0');
                                        const fd = String(f.date.getDate()).padStart(2, '0');
                                        return isInRange(`${fy}-${fm}-${fd}`);
                                    }
                                    return false;
                                }
                                // 레거시: followUp가 문자열/Date였던 경우를 대비
                                if (typeof f === 'string') return isInRange(f);
                                if (f instanceof Date) {
                                    const fy = String(f.getFullYear());
                                    const fm = String(f.getMonth() + 1).padStart(2, '0');
                                    const fd = String(f.getDate()).padStart(2, '0');
                                    return isInRange(`${fy}-${fm}-${fd}`);
                                }
                                return false;
                            });
                            return hasUpcoming ? c : null;
                        } catch (_) {
                            return null;
                        }
                    })
                );
                for (const r of results) if (r) fromHistories.push(r);
            } catch (_) { }

            // 고객 필드에 설정된 Follow Up
            const fromCustomerField = allCustomers.filter(c => {
                const val = c.followUpDate || c.followUpPlanDate || c.followUp || null;
                if (!val) return false;
                if (typeof val === 'string') return isInRange(val);
                if (val instanceof Date) {
                    const vyyyy = String(val.getFullYear());
                    const vmm = String(val.getMonth() + 1).padStart(2, '0');
                    const vdd = String(val.getDate()).padStart(2, '0');
                    return isInRange(`${vyyyy}-${vmm}-${vdd}`);
                }
                return false;
            });

            // 합치고 중복 제거
            const map = new Map();
            [...fromCustomerField, ...fromHistories].forEach(c => {
                if (c && c.id) map.set(c.id, c);
            });
            const merged = Array.from(map.values());

            // 각 고객의 다가오는 FollowUp 상세(메모/날짜/남은일수) 수집
            try {
                const details = {};
                const customersWithDates = [];
                
                await Promise.all(
                    merged.map(async (c) => {
                        try {
                            const hist = await getAllCustomerHistory(c.id);
                            const customerFollowUps = [];
                            
                            // 히스토리에서 다가오는 Follow Up 찾기
                            if (Array.isArray(hist)) {
                                hist.forEach(h => {
                                    const f = h?.followUp;
                                    if (!f) return;
                                    
                                    let followUpDate = null;
                                    if (typeof f === 'object' && f?.date) {
                                        followUpDate = typeof f.date === 'string' ? f.date.slice(0, 10) : null;
                                        if (followUpDate && isInRange(followUpDate)) {
                                            const dateObj = new Date(followUpDate);
                                            const daysLeft = daysBetween(today, dateObj);
                                            customerFollowUps.push({
                                                content: f.content || '',
                                                date: followUpDate,
                                                daysLeft: daysLeft
                                            });
                                        }
                                    } else if (typeof f === 'string' && isInRange(f)) {
                                        const dateStr = f.slice(0, 10);
                                        const dateObj = new Date(dateStr);
                                        const daysLeft = daysBetween(today, dateObj);
                                        customerFollowUps.push({
                                            content: '',
                                            date: dateStr,
                                            daysLeft: daysLeft
                                        });
                                    }
                                });
                            }
                            
                            // 고객 필드에서 다가오는 Follow Up 찾기
                            const val = c.followUpDate || c.followUpPlanDate || c.followUp || null;
                            if (val) {
                                let dateStr = null;
                                if (typeof val === 'string' && isInRange(val)) {
                                    dateStr = val.slice(0, 10);
                                } else if (val instanceof Date) {
                                    const vyyyy = String(val.getFullYear());
                                    const vmm = String(val.getMonth() + 1).padStart(2, '0');
                                    const vdd = String(val.getDate()).padStart(2, '0');
                                    dateStr = `${vyyyy}-${vmm}-${vdd}`;
                                    if (!isInRange(dateStr)) dateStr = null;
                                }
                                
                                if (dateStr) {
                                    const dateObj = new Date(dateStr);
                                    const daysLeft = daysBetween(today, dateObj);
                                    // 히스토리에 같은 날짜가 없으면 추가
                                    if (!customerFollowUps.some(fu => fu.date === dateStr)) {
                                        customerFollowUps.push({
                                            content: '',
                                            date: dateStr,
                                            daysLeft: daysLeft
                                        });
                                    }
                                }
                            }
                            
                            if (customerFollowUps.length > 0) {
                                // 가장 가까운 날짜의 Follow Up을 기준으로 고객 정렬
                                const minDaysLeft = Math.min(...customerFollowUps.map(fu => fu.daysLeft));
                                customersWithDates.push({
                                    customer: c,
                                    minDaysLeft: minDaysLeft,
                                    followUps: customerFollowUps
                                });
                                details[c.id] = customerFollowUps;
                            }
                        } catch (_) { }
                    })
                );
                
                // 날짜순으로 정렬 (가까운 날짜부터)
                customersWithDates.sort((a, b) => a.minDaysLeft - b.minDaysLeft);
                setUpcomingTodoCustomers(customersWithDates.map(item => item.customer));
                setUpcomingFollowUpMap(details);
            } catch (_) { 
                setUpcomingTodoCustomers([]);
                setUpcomingFollowUpMap({});
            }

        } catch (error) {
            console.error('다가오는 할일 로드 실패:', error);
            // 폴백: 메모리의 customers 사용
            const allCustomers = Object.values(customers).flat();
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const sevenDaysLater = new Date(today);
            sevenDaysLater.setDate(sevenDaysLater.getDate() + 7);

            // 날짜 범위 내의 모든 날짜 문자열 생성
            const dateStrings = [];
            for (let i = 1; i <= 7; i++) {
                const date = new Date(today);
                date.setDate(date.getDate() + i);
                const yyyy = String(date.getFullYear());
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                dateStrings.push(`${yyyy}-${mm}-${dd}`);
            }

            const isInRange = (dateStr) => {
                if (!dateStr) return false;
                const dateOnly = typeof dateStr === 'string' ? dateStr.slice(0, 10) : dateStr;
                return dateStrings.includes(dateOnly);
            };

            // 고객 필드 + 고객 히스토리의 Follow Up 병합
            const fromHistories = [];
            try {
                const results = await Promise.all(
                    allCustomers.map(async (c) => {
                        try {
                            const hist = await getAllCustomerHistory(c.id);
                            if (!Array.isArray(hist)) return null;
                            const hasUpcoming = hist.some(h => {
                                const f = h?.followUp;
                                if (!f) return false;
                                if (typeof f === 'object' && f?.date) {
                                    if (typeof f.date === 'string') return isInRange(f.date);
                                    if (f.date instanceof Date) {
                                        const fy = String(f.date.getFullYear());
                                        const fm = String(f.date.getMonth() + 1).padStart(2, '0');
                                        const fd = String(f.date.getDate()).padStart(2, '0');
                                        return isInRange(`${fy}-${fm}-${fd}`);
                                    }
                                    return false;
                                }
                                if (typeof f === 'string') return isInRange(f);
                                if (f instanceof Date) {
                                    const fy = String(f.getFullYear());
                                    const fm = String(f.getMonth() + 1).padStart(2, '0');
                                    const fd = String(f.getDate()).padStart(2, '0');
                                    return isInRange(`${fy}-${fm}-${fd}`);
                                }
                                return false;
                            });
                            return hasUpcoming ? c : null;
                        } catch (_) {
                            return null;
                        }
                    })
                );
                for (const r of results) if (r) fromHistories.push(r);
            } catch (_) { }

            // 고객 필드에 설정된 Follow Up
            const fromCustomerField = allCustomers.filter(c => {
                const val = c.followUpDate || c.followUpPlanDate || c.followUp || null;
                if (!val) return false;
                if (typeof val === 'string') return isInRange(val);
                if (val instanceof Date) {
                    const vyyyy = String(val.getFullYear());
                    const vmm = String(val.getMonth() + 1).padStart(2, '0');
                    const vdd = String(val.getDate()).padStart(2, '0');
                    return isInRange(`${vyyyy}-${vmm}-${vdd}`);
                }
                return false;
            });

            // 합치고 중복 제거
            const map = new Map();
            [...fromCustomerField, ...fromHistories].forEach(c => {
                if (c && c.id) map.set(c.id, c);
            });
            const merged = Array.from(map.values());

            // 각 고객의 다가오는 FollowUp 상세 수집
            try {
                const details = {};
                const customersWithDates = [];
                
                await Promise.all(
                    merged.map(async (c) => {
                        try {
                            const hist = await getAllCustomerHistory(c.id);
                            const customerFollowUps = [];
                            
                            if (Array.isArray(hist)) {
                                hist.forEach(h => {
                                    const f = h?.followUp;
                                    if (!f) return;
                                    
                                    let followUpDate = null;
                                    if (typeof f === 'object' && f?.date) {
                                        followUpDate = typeof f.date === 'string' ? f.date.slice(0, 10) : null;
                                        if (followUpDate && isInRange(followUpDate)) {
                                            const dateObj = new Date(followUpDate);
                                            const daysLeft = daysBetween(today, dateObj);
                                            customerFollowUps.push({
                                                content: f.content || '',
                                                date: followUpDate,
                                                daysLeft: daysLeft
                                            });
                                        }
                                    } else if (typeof f === 'string' && isInRange(f)) {
                                        const dateStr = f.slice(0, 10);
                                        const dateObj = new Date(dateStr);
                                        const daysLeft = daysBetween(today, dateObj);
                                        customerFollowUps.push({
                                            content: '',
                                            date: dateStr,
                                            daysLeft: daysLeft
                                        });
                                    }
                                });
                            }
                            
                            const val = c.followUpDate || c.followUpPlanDate || c.followUp || null;
                            if (val) {
                                let dateStr = null;
                                if (typeof val === 'string' && isInRange(val)) {
                                    dateStr = val.slice(0, 10);
                                } else if (val instanceof Date) {
                                    const vyyyy = String(val.getFullYear());
                                    const vmm = String(val.getMonth() + 1).padStart(2, '0');
                                    const vdd = String(val.getDate()).padStart(2, '0');
                                    dateStr = `${vyyyy}-${vmm}-${vdd}`;
                                    if (!isInRange(dateStr)) dateStr = null;
                                }
                                
                                if (dateStr) {
                                    const dateObj = new Date(dateStr);
                                    const daysLeft = daysBetween(today, dateObj);
                                    if (!customerFollowUps.some(fu => fu.date === dateStr)) {
                                        customerFollowUps.push({
                                            content: '',
                                            date: dateStr,
                                            daysLeft: daysLeft
                                        });
                                    }
                                }
                            }
                            
                            if (customerFollowUps.length > 0) {
                                const minDaysLeft = Math.min(...customerFollowUps.map(fu => fu.daysLeft));
                                customersWithDates.push({
                                    customer: c,
                                    minDaysLeft: minDaysLeft,
                                    followUps: customerFollowUps
                                });
                                details[c.id] = customerFollowUps;
                            }
                        } catch (_) { }
                    })
                );
                
                customersWithDates.sort((a, b) => a.minDaysLeft - b.minDaysLeft);
                setUpcomingTodoCustomers(customersWithDates.map(item => item.customer));
                setUpcomingFollowUpMap(details);
            } catch (_) { 
                setUpcomingTodoCustomers([]);
                setUpcomingFollowUpMap({});
            }
        }
    }, [customers]);

    // 히스토리 인사이트 생성
    const generateInsights = useCallback(() => {
        const allCustomers = Object.values(customers).flat();
        const insightsList = [];

        const getUpcomingBirthdayCustomers = (allCustomers) => {
            const today = new Date();
            const list = allCustomers.filter(c => !!c.birthdate).map(c => {
                let birthMonth, birthDate;
                if (typeof c.birthdate === 'string') {
                    const match = c.birthdate.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (match) {
                        birthMonth = parseInt(match[2], 10) - 1;
                        birthDate = parseInt(match[3], 10);
                    } else {
                        const birth = new Date(c.birthdate);
                        birthMonth = birth.getMonth();
                        birthDate = birth.getDate();
                    }
                } else {
                    const birth = new Date(c.birthdate);
                    birthMonth = birth.getMonth();
                    birthDate = birth.getDate();
                }

                const thisYear = new Date(today.getFullYear(), birthMonth, birthDate);
                const next = thisYear < today ? new Date(today.getFullYear() + 1, birthMonth, birthDate) : thisYear;
                const dLeft = daysBetween(today, next);
                return { customer: c, daysLeft: dLeft };
            }).filter(x => x.daysLeft > 0 && x.daysLeft <= 30)
                .sort((a, b) => a.daysLeft - b.daysLeft)
                .slice(0, 3)
                .map(x => x.customer);
            return list;
        };

        const getContactOver6MonthsCustomers = (allCustomers) => {
            const today = new Date();
            const sixMonths = 180;
            const list = allCustomers.filter(c => {
                const last = getLastHistoryDate(c.id);
                if (!last) return true;
                return daysBetween(last, today) >= sixMonths;
            }).slice(0, 3);
            return list;
        };

        const getOldInBankCustomers = (customersByStage) => {
            const today = new Date();
            const sixMonths = 180;
            const bankList = (customersByStage['bank'] || []);
            const filtered = bankList.filter(c => {
                const last = getLastHistoryDate(c.id);
                if (!last) return true;
                return daysBetween(last, today) >= sixMonths;
            }).slice(0, 3);
            return filtered;
        };

        // 1. 최근 활동이 많은 고객
        const recentActiveCustomers = allCustomers.filter(customer => {
            // 최근 7일 내에 히스토리가 있는 고객 (실제로는 히스토리 데이터를 확인해야 함)
            return customer.priority === 'high';
        });

        if (recentActiveCustomers.length > 0) {
            insightsList.push({
                type: 'activity',
                title: '활발한 고객',
                description: `우선순위가 높은 고객 ${recentActiveCustomers.length}명이 있습니다.`,
                customers: recentActiveCustomers.slice(0, 3),
                icon: TrendingUp,
                color: 'from-emerald-500 to-teal-600'
            });
        }

        // 2. 연락이 필요한 고객
        const contactNeededCustomers = allCustomers.filter(customer => {
            // 예시: 가치가 높지만 우선순위가 낮은 고객
            return customer.value > 10000000 && customer.priority === 'low';
        });

        if (contactNeededCustomers.length > 0) {
            insightsList.push({
                type: 'contact',
                title: '연락 권장',
                description: `높은 가치를 가진 고객 ${contactNeededCustomers.length}명에게 연락을 권장합니다.`,
                customers: contactNeededCustomers.slice(0, 3),
                icon: Phone,
                color: 'from-blue-500 to-indigo-600'
            });
        }

        // 3. 랜덤 추천: 제시된 3가지 기준 중 하나를 선택해 추천
        const candidates = [];
        const upcomingBirthdays = getUpcomingBirthdayCustomers(allCustomers);
        if (upcomingBirthdays.length > 0) {
            candidates.push({
                key: 'birthdaySoon',
                title: '곧 생일인 고객 추천',
                description: '다가오는 생일 고객에게 미리 축하 인사를 전해보세요.',
                customers: upcomingBirthdays,
                icon: Gift,
                color: 'from-rose-500 to-pink-600'
            });
        }

        const staleContacts = getContactOver6MonthsCustomers(allCustomers);
        if (staleContacts.length > 0) {
            candidates.push({
                key: 'staleContacts',
                title: '오랫동안 연락하지 않은 고객',
                description: '6개월 이상 연락이 없었던 고객에게 안부를 전해보세요.',
                customers: staleContacts,
                icon: Phone,
                color: 'from-blue-500 to-indigo-600'
            });
        }

        const oldBanks = getOldInBankCustomers(customers);
        if (oldBanks.length > 0) {
            candidates.push({
                key: 'oldBank',
                title: '고객뱅크에서 오래된 고객',
                description: '오랜 기간 보관 중인 고객을 다시 활성화해보세요.',
                customers: oldBanks,
                icon: Clock,
                color: 'from-slate-500 to-gray-600'
            });
        }

        if (candidates.length > 0) {
            const pick = candidates[Math.floor(Math.random() * candidates.length)];
            insightsList.push({
                type: 'recommend',
                title: pick.title,
                description: pick.description,
                customers: pick.customers,
                icon: pick.icon,
                color: pick.color
            });
        }

        // 4. 심리/행동경제학 기반 영업 팁 (풍부한 콘텐츠 + 실전 스크립트)
        const psychologyTips = [
            {
                type: 'tip',
                title: 'Peak–End Rule 활용',
                category: '상담심리학',
                applicableStages: ['consultation', 'closing'],
                description: '상담의 하이라이트와 마지막 인상에 집중하세요. 마무리 3분에 핵심 가치를 재정리하고, 다음 액션(콜백/미팅)을 명확히 합의하면 재기억률이 크게 올라갑니다. 스크립트: “오늘 대화에서 가장 가치있었던 포인트를 두 가지만 정리드리면… 다음으로는 언제 어떤 방식으로 진행하면 좋을까요?”',
                icon: Lightbulb,
                color: 'from-amber-500 to-orange-600'
            },
            {
                type: 'tip',
                title: 'Commitment & Consistency',
                category: '설득심리',
                applicableStages: ['preparation', 'appointment', 'consultation'],
                description: '작은 동의부터 시작해 점진적으로 확장하세요. “5분만 투자하셔서 현재 보장 공백만 체크해볼까요?” 같은 미세 약속은 큰 결정을 위한 정합성 압력을 만듭니다.',
                icon: CheckCircle,
                color: 'from-emerald-500 to-teal-600'
            },
            {
                type: 'tip',
                title: 'Loss Aversion 프레이밍',
                category: '행동경제학',
                applicableStages: ['consultation', 'closing'],
                description: '획득보다 손실 회피가 더 강하게 작동합니다. “지금 유지하시는 조건이 바뀌면 잃게 되는 혜택”을 수치/사례로 명확히 제시하세요. 스크립트: “현재 조건이 바뀌면 연간 최대 ___원을 놓치실 수 있어요.”',
                icon: TrendingUp,
                color: 'from-red-500 to-rose-600'
            },
            {
                type: 'tip',
                title: 'Social Proof 정밀 사용',
                category: '사회적 증거',
                applicableStages: ['appointment', 'consultation', 'closing'],
                description: '유사한 특성(연령/직군/가족상황)을 가진 고객의 사례를 제시하세요. “같은 업의 A대표님도 최근에 이 위험 구간을 정리하셨어요.”처럼 구체성 있는 사회적 증거가 효과적입니다.',
                icon: Users,
                color: 'from-indigo-500 to-purple-600'
            },
            {
                type: 'tip',
                title: 'Anchoring(기준점) 설정',
                category: '행동경제학',
                applicableStages: ['consultation', 'closing'],
                description: '먼저 상위 옵션의 가치를 보여준 후 실속형 대안을 제시하세요. 상위 안이 기준점이 되어, 현실적인 안이 상대적으로 합리적으로 느껴집니다. 3-옵션 구조(프리미엄/밸런스/에센셜)가 안전합니다.',
                icon: Target,
                color: 'from-blue-500 to-cyan-600'
            },
            {
                type: 'tip',
                title: 'Temporal Landmarks 시작 효과',
                category: '행동과학',
                applicableStages: ['preparation', 'appointment'],
                description: '월초/분기초/생일 이후 등 “새 출발 지점”에서 행동 의지를 높이세요. 메시지: “이번 달부터 재무장부를 정돈하는 첫 달로 만들어볼까요?”',
                icon: Calendar,
                color: 'from-teal-500 to-green-600'
            },
            {
                type: 'tip',
                title: 'Question–Led Selling',
                category: '상담기법',
                applicableStages: ['consultation'],
                description: '설득보다 탐색을 늘리세요. 80/20 규칙: 경청 80%, 제안 20%. 핵심 질문: “가장 걱정되는 시나리오는 무엇인가요?”, “이 결정에서 가장 중요한 기준은 3가지만 꼽으면 무엇일까요?”',
                icon: MessageCircle,
                color: 'from-sky-500 to-blue-600'
            },
            {
                type: 'tip',
                title: 'Implementation Intentions',
                category: '행동설계',
                applicableStages: ['appointment', 'closing'],
                description: '“언제, 어디서, 어떻게”를 문장으로 고정하세요. 예: “목요일 점심시간(12:30)에 10분만 통화해 최종안 확정하기.” 구체화된 계획은 행동 전환률을 높입니다.',
                icon: Clock,
                color: 'from-slate-500 to-gray-600'
            },
            {
                type: 'tip',
                title: 'Default 옵션의 힘',
                category: '행동경제학',
                applicableStages: ['consultation', 'closing'],
                description: '결정 피로를 줄이기 위해 “권장(기본)안”을 제시하고, 필요 시 조정하는 흐름을 만드세요. “대부분의 ___ 고객분들은 이 구성을 기본으로 시작하십니다.”',
                icon: Star,
                color: 'from-yellow-500 to-amber-600'
            },
            {
                type: 'tip',
                title: 'Reciprocity(호혜성) 트리거',
                category: '설득심리',
                applicableStages: ['preparation', 'appointment', 'consultation'],
                description: '고객에게 바로 도움이 되는 리소스를 먼저 제공하세요(보장 공백 체크리스트/세금 캘린더). 작은 호혜 자극이 후속 행동을 촉진합니다.',
                icon: Gift,
                color: 'from-pink-500 to-rose-600'
            }
            , {
                type: 'tip',
                title: 'Priming(점화) 문구',
                category: '인지심리',
                applicableStages: ['preparation'],
                description: '연락 첫 문장에 맥락 신호를 심어두세요. “요즘 리스크 관리 이슈가 많아 간단 체크만 도와드리려 합니다.”와 같이 주제에 대한 인지 틀을 형성합니다.',
                icon: MessageCircle,
                color: 'from-sky-500 to-blue-600'
            }
            , {
                type: 'tip',
                title: 'Authority(전문성) 신뢰',
                category: '설득심리',
                applicableStages: ['consultation'],
                description: '자격/경력/데이터 출처를 간결히 제시하세요. “최근 3년간 200+ 케이스를 점검하며 발견된 평균 리스크는…”과 같이 객관 지표를 동반합니다.',
                icon: Users,
                color: 'from-indigo-500 to-purple-600'
            }
            , {
                type: 'tip',
                title: 'Salience(현저성) 강조',
                category: '인지심리',
                applicableStages: ['consultation'],
                description: '고객 상황과 직접 연결되는 수치/사례만 남기고 나머지는 제거하세요. 하이라이트/아이콘/여백으로 핵심을 도드라지게 만듭니다.',
                icon: Lightbulb,
                color: 'from-amber-500 to-orange-600'
            }
            , {
                type: 'tip',
                title: 'Pre-commit 약속 캘린더',
                category: '행동설계',
                applicableStages: ['appointment', 'closing'],
                description: '다음 액션을 일정으로 고정하세요. “목 12:30, 10분 통화로 최종 확정”처럼 캘린더 초대를 먼저 제안합니다.',
                icon: Calendar,
                color: 'from-teal-500 to-green-600'
            }
            , {
                type: 'tip',
                title: '전화 대신 짧은 보이스',
                category: '상담기법',
                applicableStages: ['preparation'],
                description: '텍스트 대신 20~30초 보이스메시지로 톤/진심을 전달하세요. 응답률이 유의미하게 상승합니다.',
                icon: Phone,
                color: 'from-emerald-500 to-teal-600'
            }
            , {
                type: 'tip',
                title: 'Pros/Cons 2×2 요약',
                category: '행동설계',
                applicableStages: ['consultation'],
                description: '장점/단점/리스크/대안 2×2 매트릭스로 시각화하면 인지부하가 줄어 빠른 결정을 돕습니다.',
                icon: Target,
                color: 'from-blue-500 to-cyan-600'
            }
            , {
                type: 'tip',
                title: 'Reference Class Forecasting',
                category: '행동경제학',
                applicableStages: ['consultation'],
                description: '개별 사례 대신 “동일 집단의 평균 결과”를 기반으로 전망을 제시하세요. 낙관/비관 편향을 줄입니다.',
                icon: TrendingUp,
                color: 'from-green-500 to-emerald-600'
            }
            , {
                type: 'tip',
                title: 'Implementation Check-list',
                category: '행동설계',
                applicableStages: ['closing'],
                description: '체결 전 “필요 서류/시간/다음 단계” 체크리스트를 제공해 마찰을 제거하세요. 탈락률이 감소합니다.',
                icon: CheckCircle,
                color: 'from-emerald-500 to-teal-600'
            }
            , {
                type: 'tip',
                title: 'Goal Gradient(목표 구배)',
                category: '행동과학',
                applicableStages: ['appointment', 'consultation'],
                description: '진행률 바/체크포인트를 보여주면 마무리 속도가 빨라집니다. “3단계 중 2단계 완료(67%)” 시각화.',
                icon: Clock,
                color: 'from-slate-500 to-gray-600'
            }
            , {
                type: 'tip',
                title: 'Positive Presupposition',
                category: '상담기법',
                applicableStages: ['closing'],
                description: '확정 전제를 깐 질문: “A안과 B안 중 어느 쪽이 생활 패턴에 맞으세요?” 이분법 대신 고객 기준을 묻습니다.',
                icon: MessageCircle,
                color: 'from-sky-500 to-blue-600'
            }
            , {
                type: 'tip',
                title: 'Regret Minimization',
                category: '행동경제학',
                applicableStages: ['closing'],
                description: '“1년 뒤 돌아봤을 때 어떤 선택이 후회가 적을까요?” 미래 관점의 자기 대화를 유도합니다.',
                icon: Lightbulb,
                color: 'from-amber-500 to-orange-600'
            }
            , {
                type: 'tip',
                title: 'Pre-mortem(사전 부검)',
                category: '상담기법',
                applicableStages: ['consultation'],
                description: '실행이 실패했다고 가정하고 원인을 찾은 뒤, 지금 보완책을 합의합니다. 리스크 대처력이 올라갑니다.',
                icon: Target,
                color: 'from-indigo-500 to-blue-600'
            }
            , {
                type: 'tip',
                title: 'Choice Architecture 문구',
                category: '행동설계',
                applicableStages: ['consultation', 'closing'],
                description: '문구를 행동 유도형으로: “원하시면 지금 바로 ___을 시작할 수 있어요.” 선택을 쉽고 가볍게 느끼게 합니다.',
                icon: Star,
                color: 'from-yellow-500 to-amber-600'
            }
            , {
                type: 'tip',
                title: 'Sunk Cost Fallacy 경계',
                category: '행동경제학',
                applicableStages: ['consultation'],
                description: '이미 쓴 비용 때문에 비효율을 유지하지 않도록 “미래 기준의 합리성”으로 리프레이밍합니다.',
                icon: TrendingUp,
                color: 'from-red-500 to-rose-600'
            }
            , {
                type: 'tip',
                title: 'Empathy Statement',
                category: '상담심리학',
                applicableStages: ['consultation'],
                description: '공감 고정 문장 3개를 준비해두세요. “그 부분이 가장 부담되실 수 있겠네요.” 방어를 낮추고 협력을 유도합니다.',
                icon: MessageCircle,
                color: 'from-sky-500 to-blue-600'
            }
            , {
                type: 'tip',
                title: 'Consistency Reminder',
                category: '설득심리',
                applicableStages: ['closing'],
                description: '이전 합의 내용을 상기시켜 일관성 프레임을 강화. “지난 통화에서 ___을 가장 중요하게 보신다고 하셨어요.”',
                icon: CheckCircle,
                color: 'from-emerald-500 to-teal-600'
            }
            , {
                type: 'tip',
                title: 'Next Best Action 제시',
                category: '행동설계',
                applicableStages: ['appointment', 'consultation'],
                description: '항상 “다음 한 걸음”을 제시하세요. 예: “보장 공백 캡처 이미지만 보내주시면 초안 작성해 드릴게요.”',
                icon: Calendar,
                color: 'from-teal-500 to-green-600'
            }
            , {
                type: 'tip',
                title: 'Visual Anchor(시각 기준점)',
                category: '인지심리',
                applicableStages: ['consultation'],
                description: '하이라이트 색/굵기/아이콘으로 기준값(보장액/월납입)을 고정시키면 제안 비교가 쉬워집니다.',
                icon: Lightbulb,
                color: 'from-amber-500 to-orange-600'
            }
            , {
                type: 'tip',
                title: '‘왜’에서 ‘어떻게’로 전환',
                category: '상담기법',
                applicableStages: ['closing'],
                description: '이의제기가 반복되면 “왜”를 설명하기보다 실행 방법으로 전환: “그럼 이렇게 진행하면 어떨까요?”',
                icon: Target,
                color: 'from-blue-500 to-cyan-600'
            }
            , {
                type: 'tip',
                title: 'Micro-survey(1문 질문)',
                category: '행동설계',
                applicableStages: ['preparation'],
                description: '“가장 궁금하신 건 무엇인가요? A/B/C 중 선택” 한 문장 설문으로 관심사를 파악해 맞춤 제안을 준비하세요.',
                icon: MessageCircle,
                color: 'from-sky-500 to-blue-600'
            }
        ];

        // 필터 적용 후 2~3개 랜덤 샘플링
        const shuffled = [...psychologyTips].sort(() => Math.random() - 0.5);
        const tipCount = Math.min(3, 2 + Math.floor(Math.random() * 2));
        insightsList.push(...shuffled.slice(0, tipCount));

        setInsights(insightsList);
    }, [customers]);

    const formatMoney = (amount) => {
        if (amount >= 100000000) {
            return Math.round(amount / 100000000) + '억원';
        } else if (amount >= 10000) {
            return Math.round(amount / 10000) + '만원';
        } else if (amount > 0) {
            return amount.toLocaleString() + '원';
        }
        return '미정';
    };

    const getStageName = (customer) => {
        // 고객이 어느 단계에 있는지 찾기
        for (const [stageId, stageCustomers] of Object.entries(customers)) {
            if (stageCustomers.some(c => c.id === customer.id)) {
                const stageNames = {
                    'prospect': '가망고객',
                    'preparation': '연락대상',
                    'appointment': '약속확정',
                    'consultation': '상담완료',
                    'closed': '계약완료',
                    'rejected': '보류/거절',
                    'bank': '고객뱅크'
                };
                return stageNames[stageId] || stageId;
            }
        }
        return '알 수 없음';
    };

    // 날짜별 할일 개수 계산 (해당 월 전체)
    const getTodosByDate = useCallback(async () => {
        const todosByDate = {};
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        
        // 해당 월의 첫 날과 마지막 날
        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        const firstDayStr = `${String(firstDay.getFullYear())}-${String(firstDay.getMonth() + 1).padStart(2, '0')}-${String(firstDay.getDate()).padStart(2, '0')}`;
        const lastDayStr = `${String(lastDay.getFullYear())}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
        
        try {
            // 모든 고객 조회
            const { idbGetAllCustomers } = await import('../utils/idb');
            const allCustomers = await idbGetAllCustomers();
            
            // 각 고객의 해당 월 Follow Up 찾기
            await Promise.all(
                allCustomers.map(async (customer) => {
                    let hist = null;
                    // 히스토리에서 해당 월의 Follow Up 찾기
                    try {
                        hist = await getAllCustomerHistory(customer.id);
                        if (Array.isArray(hist)) {
                            hist.forEach(h => {
                                const f = h?.followUp;
                                if (!f) return;
                                
                                let followUpDate = null;
                                if (typeof f === 'object' && f?.date) {
                                    followUpDate = typeof f.date === 'string' ? f.date.slice(0, 10) : null;
                                } else if (typeof f === 'string') {
                                    followUpDate = f.slice(0, 10);
                                }
                                
                                // 해당 월 범위 내인지 확인
                                if (followUpDate && followUpDate >= firstDayStr && followUpDate <= lastDayStr) {
                                    todosByDate[followUpDate] = (todosByDate[followUpDate] || 0) + 1;
                                }
                            });
                        }
                    } catch (_) { }
                    
                    // 고객 필드에서 해당 월의 Follow Up 찾기
                    const val = customer.followUpDate || customer.followUpPlanDate || customer.followUp || null;
                    if (val) {
                        let dateStrFromField = null;
                        if (typeof val === 'string') {
                            dateStrFromField = val.slice(0, 10);
                        } else if (val instanceof Date) {
                            const vyyyy = String(val.getFullYear());
                            const vmm = String(val.getMonth() + 1).padStart(2, '0');
                            const vdd = String(val.getDate()).padStart(2, '0');
                            dateStrFromField = `${vyyyy}-${vmm}-${vdd}`;
                        }
                        
                        // 해당 월 범위 내인지 확인
                        if (dateStrFromField && dateStrFromField >= firstDayStr && dateStrFromField <= lastDayStr) {
                            // 히스토리에 같은 날짜의 Follow Up이 있는지 확인
                            const hasInHistory = Array.isArray(hist) && hist.some(h => {
                                const f = h?.followUp;
                                if (!f) return false;
                                let followUpDate = null;
                                if (typeof f === 'object' && f?.date) {
                                    followUpDate = typeof f.date === 'string' ? f.date.slice(0, 10) : null;
                                } else if (typeof f === 'string') {
                                    followUpDate = f.slice(0, 10);
                                }
                                return followUpDate === dateStrFromField;
                            });
                            
                            // 히스토리에 없으면 고객 필드의 Follow Up 추가
                            if (!hasInHistory) {
                                todosByDate[dateStrFromField] = (todosByDate[dateStrFromField] || 0) + 1;
                            }
                        }
                    }
                })
            );
        } catch (error) {
            console.error('날짜별 할일 개수 계산 실패:', error);
        }

        return todosByDate;
    }, []);

    // 선택된 날짜의 할일 목록 가져오기
    const getTodosForDate = useCallback(async (dateStr) => {
        const todos = [];
        if (!dateStr) return todos;

        try {
            // 모든 고객 조회
            const { idbGetAllCustomers } = await import('../utils/idb');
            const allCustomers = await idbGetAllCustomers();
            
            // 각 고객의 해당 날짜 할일 찾기
            await Promise.all(
                allCustomers.map(async (customer) => {
                    const matchingFollowUps = [];
                    
                    // 히스토리에서 해당 날짜의 Follow Up 찾기
                    try {
                        const hist = await getAllCustomerHistory(customer.id);
                        if (Array.isArray(hist)) {
                            hist.forEach(h => {
                                const f = h?.followUp;
                                if (!f) return;
                                
                                let followUpDate = null;
                                if (typeof f === 'object' && f?.date) {
                                    followUpDate = typeof f.date === 'string' ? f.date.slice(0, 10) : null;
                                } else if (typeof f === 'string') {
                                    followUpDate = f.slice(0, 10);
                                }
                                
                                if (followUpDate === dateStr) {
                                    matchingFollowUps.push({
                                        content: (typeof f === 'object' ? f.content : '') || '',
                                        date: followUpDate
                                    });
                                }
                            });
                        }
                    } catch (_) { }
                    
                    // 고객 필드에서 해당 날짜 찾기
                    const val = customer.followUpDate || customer.followUpPlanDate || customer.followUp || null;
                    if (val) {
                        let dateStrFromField = null;
                        if (typeof val === 'string') {
                            dateStrFromField = val.slice(0, 10);
                        } else if (val instanceof Date) {
                            const vyyyy = String(val.getFullYear());
                            const vmm = String(val.getMonth() + 1).padStart(2, '0');
                            const vdd = String(val.getDate()).padStart(2, '0');
                            dateStrFromField = `${vyyyy}-${vmm}-${vdd}`;
                        }
                        
                        if (dateStrFromField === dateStr && !matchingFollowUps.some(fu => fu.date === dateStr)) {
                            matchingFollowUps.push({
                                content: '',
                                date: dateStr
                            });
                        }
                    }
                    
                    if (matchingFollowUps.length > 0) {
                        todos.push({
                            customer: customer,
                            followUps: matchingFollowUps
                        });
                    }
                })
            );
        } catch (error) {
            console.error('날짜별 할일 조회 실패:', error);
        }

        return todos;
    }, []);

    useEffect(() => {
        const loadData = async () => {
            await loadBirthdayCustomers();
            loadTodayTodos();
            await loadUpcomingTodos();
            generateInsights();
            // 날짜별 할일 개수 로드
            const todosByDateData = await getTodosByDate();
            setTodosByDate(todosByDateData);
        };
        loadData();
    }, [customers, loadBirthdayCustomers, loadTodayTodos, loadUpcomingTodos, generateInsights, getTodosByDate]);

    // 날짜 클릭 핸들러
    const handleDateClick = async (dateStr) => {
        setSelectedDate(dateStr);
        setShowDateModal(true);
        setLoadingDateTodos(true);
        try {
            const todos = await getTodosForDate(dateStr);
            setDateTodos(todos);
        } catch (error) {
            console.error('날짜별 할일 로드 실패:', error);
            setDateTodos([]);
        } finally {
            setLoadingDateTodos(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden shadow-2xl border border-white/20 flex flex-col">
                {/* 헤더 */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 sm:p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                                <Users className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">AI 비서</h2>
                                <p className="text-gray-600 mt-1 text-sm">오늘의 영업 인사이트와 고객 정보를 확인하세요</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-300"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* 탭 메뉴 */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('birthday')}
                        className={`flex-1 py-3 sm:py-4 px-4 sm:px-6 text-center font-bold transition-all duration-300 ${activeTab === 'birthday'
                            ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <Calendar className="w-4 h-4" />
                            생일 ({birthdayCustomers.length})
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('todo')}
                        className={`flex-1 py-3 sm:py-4 px-4 sm:px-6 text-center font-bold transition-all duration-300 ${activeTab === 'todo'
                            ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <ListChecks className="w-4 h-4" />
                            할일 ({todayTodoCustomers.length})
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('insights')}
                        className={`flex-1 py-3 sm:py-4 px-4 sm:px-6 text-center font-bold transition-all duration-300 ${activeTab === 'insights'
                            ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <Lightbulb className="w-4 h-4" />
                            아이디어
                        </div>
                    </button>
                </div>

                {/* 콘텐츠 */}
                <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                    {activeTab === 'birthday' && (
                        <div>
                            {birthdayCustomers.length === 0 ? (
                                <div className="text-center py-12">
                                    <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-lg font-bold text-gray-600 mb-2">오늘 생일인 고객이 없습니다</h3>
                                    <p className="text-gray-500">고객 정보에 생년월일을 입력하면 생일 알림을 받을 수 있습니다.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-200 rounded-xl p-4 mb-6">
                                        <div className="flex items-center gap-3">
                                            <Gift className="w-6 h-6 text-pink-600" />
                                            <div>
                                                <h3 className="font-bold text-pink-800">🎉 축하합니다!</h3>
                                                <p className="text-pink-700 text-sm">오늘 {birthdayCustomers.length}명의 고객이 생일입니다.</p>
                                            </div>
                                        </div>
                                    </div>

                                    {birthdayCustomers.map((customer) => (
                                        <div
                                            key={customer.id}
                                            className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                            onClick={() => onSelectCustomer && onSelectCustomer(customer)}
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                                                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0">
                                                        {customer.name.charAt(0)}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h4 className="font-bold text-gray-800 text-base sm:text-lg truncate">{customer.name}</h4>
                                                        <p className="text-gray-600 text-xs sm:text-sm truncate">{customer.phone}</p>
                                                        {customer.company && (
                                                            <p className="text-gray-500 text-xs sm:text-sm truncate">{customer.company}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <div className="text-xs sm:text-sm text-gray-500 mb-1">{getStageName(customer)}</div>
                                                    {customer.value > 0 && (
                                                        <div className="text-green-600 font-bold text-sm sm:text-base">{formatMoney(customer.value)}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'insights' && (
                        <div className="space-y-4">
                            {insights.map((insight, index) => (
                                <div key={index} className="group relative overflow-hidden bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                    {/* 배경 그라데이션 오버레이 */}
                                    <div className={`absolute inset-0 bg-gradient-to-br ${insight.color} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>

                                    {/* 메인 콘텐츠 */}
                                    <div className="relative p-5 sm:p-6">
                                        <div className="flex items-start gap-3">
                                            {/* 텍스트 영역 (아이콘 컬럼 제거) */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    {insight.category && (
                                                        <span className="text-[10px] sm:text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded-full border border-gray-200">
                                                            {insight.category}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="font-bold text-gray-900 text-base sm:text-lg">{insight.title}</h3>
                                                    <div className={`w-2 h-2 bg-gradient-to-r ${insight.color} rounded-full`}></div>
                                                </div>
                                                <p className="text-gray-600 mb-4 leading-relaxed">{insight.description}</p>

                                                {/* 고객 리스트 */}
                                                {insight.customers && insight.customers.length > 0 && (
                                                    <div className="space-y-2 sm:space-y-3">
                                                        {insight.customers.map((customer) => (
                                                            <div
                                                                key={customer.id}
                                                                className="bg-gray-50 hover:bg-gray-100 rounded-xl p-4 transition-colors duration-200 border border-gray-100 cursor-pointer"
                                                                onClick={() => onSelectCustomer && onSelectCustomer(customer)}
                                                            >
                                                                <div className="flex items-center justify-between gap-3">
                                                                    <div className="flex items-center gap-3 min-w-0">
                                                                        <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                                                                            {customer.name.charAt(0)}
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <div className="font-semibold text-gray-900 truncate">{customer.name}</div>
                                                                            <div className="text-xs sm:text-sm text-gray-500 truncate">{customer.phone}</div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-right flex-shrink-0">
                                                                        <div className="text-[10px] sm:text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                                                                            {getStageName(customer)}
                                                                        </div>
                                                                        {customer.value > 0 && (
                                                                            <div className="text-xs sm:text-sm font-bold text-green-600 mt-1">
                                                                                {formatMoney(customer.value)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* 하단 액센트 바 */}
                                    <div className={`h-1 bg-gradient-to-r ${insight.color} opacity-60`}></div>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeTab === 'todo' && (
                        <div className="space-y-6">
                            {/* 오늘의 할일 섹션 */}
                            {todayTodoCustomers.length === 0 ? (
                                <div className="text-center py-12">
                                    <ListChecks className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-lg font-bold text-gray-600 mb-2">오늘 예정된 Follow Up이 없습니다</h3>
                                    <p className="text-gray-500">고객 카드의 Follow Up 계획 날짜를 설정하면 이곳에 표시됩니다.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
                                        <div className="flex items-center gap-3">
                                            <Clock className="w-6 h-6 text-amber-600" />
                                            <div>
                                                <h3 className="font-bold text-amber-800">⏰ 오늘의 할일</h3>
                                                <p className="text-amber-700 text-sm">오늘 Follow Up 대상 고객 {todayTodoCustomers.length}명</p>
                                            </div>
                                        </div>
                                    </div>

                                    {todayTodoCustomers.map((customer) => (
                                        <div key={customer.id} className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => onSelectCustomer && onSelectCustomer(customer)}>
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                                                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0">
                                                        {customer.name?.charAt(0) || '고'}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h4 className="font-bold text-gray-800 text-base sm:text-lg truncate">{customer.name || '고객'}</h4>
                                                        {customer.phone && (
                                                            <p className="text-gray-600 text-xs sm:text-sm truncate">{customer.phone}</p>
                                                        )}
                                                        {customer.company && (
                                                            <p className="text-gray-500 text-xs sm:text-sm truncate">{customer.company}</p>
                                                        )}
                                                        {/* 오늘자 Follow Up 목록 (여러 개 표시, 최대 5개) */}
                                                        {Array.isArray(followUpMap[customer.id]) && followUpMap[customer.id].length > 0 ? (
                                                            <ul className="mt-1 space-y-1">
                                                                {followUpMap[customer.id].slice(0, 5).map((fu, idx) => (
                                                                    <li key={idx} className="text-[12px] sm:text-sm text-amber-700 flex items-start gap-1">
                                                                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                                                        <span className="line-clamp-1">{fu.content || '메모 없음'}</span>
                                                                    </li>
                                                                ))}
                                                                {followUpMap[customer.id].length > 5 && (
                                                                    <li className="text-[12px] text-gray-500">+{followUpMap[customer.id].length - 5} 더보기</li>
                                                                )}
                                                            </ul>
                                                        ) : (
                                                            <p className="text-[12px] sm:text-sm text-gray-500 mt-1">메모 없음</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <div className="text-xs sm:text-sm text-gray-500 mb-1">{getStageName(customer)}</div>
                                                    {/* Follow Up 날짜 표시 */}
                                                    <div className="inline-flex items-center gap-1 text-xs sm:text-sm text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                                                        <Clock className="w-3 h-3" />
                                                        {(Array.isArray(followUpMap[customer.id]) && followUpMap[customer.id][0]?.date) || (customer.followUpDate || customer.followUpPlanDate || '').toString().slice(0, 10)}
                                                    </div>
                                                    {/* 빠른 액션 */}
                                                    <div className="mt-2 flex items-center justify-end gap-2">
                                                        {customer.phone && (
                                                            <a href={`tel:${customer.phone}`} onClick={(e) => e.stopPropagation()} className="text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100">전화</a>
                                                        )}
                                                        <button onClick={(e) => { e.stopPropagation(); onSelectCustomer && onSelectCustomer(customer); }} className="text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100">열기</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* 다가오는 할 일들 섹션 */}
                            {upcomingTodoCustomers.length > 0 && (
                                <div className="space-y-4 mt-8">
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6">
                                        <div className="flex items-center gap-3">
                                            <Calendar className="w-6 h-6 text-blue-600" />
                                            <div>
                                                <h3 className="font-bold text-blue-800">📅 다가오는 할 일들</h3>
                                                <p className="text-blue-700 text-sm">다음 7일 내 Follow Up 예정 고객 {upcomingTodoCustomers.length}명</p>
                                            </div>
                                        </div>
                                    </div>

                                    {upcomingTodoCustomers.map((customer) => {
                                        const upcomingFollowUps = Array.isArray(upcomingFollowUpMap[customer.id]) ? upcomingFollowUpMap[customer.id] : [];
                                        const nearestFollowUp = upcomingFollowUps.length > 0 ? upcomingFollowUps.sort((a, b) => a.daysLeft - b.daysLeft)[0] : null;
                                        
                                        return (
                                            <div key={customer.id} className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => onSelectCustomer && onSelectCustomer(customer)}>
                                                <div className="flex items-center justify-between gap-3">
                                                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0">
                                                            {customer.name?.charAt(0) || '고'}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <h4 className="font-bold text-gray-800 text-base sm:text-lg truncate">{customer.name || '고객'}</h4>
                                                            {customer.phone && (
                                                                <p className="text-gray-600 text-xs sm:text-sm truncate">{customer.phone}</p>
                                                            )}
                                                            {customer.company && (
                                                                <p className="text-gray-500 text-xs sm:text-sm truncate">{customer.company}</p>
                                                            )}
                                                            {/* 다가오는 Follow Up 목록 (여러 개 표시, 최대 5개) */}
                                                            {upcomingFollowUps.length > 0 ? (
                                                                <ul className="mt-1 space-y-1">
                                                                    {upcomingFollowUps.slice(0, 5).map((fu, idx) => (
                                                                        <li key={idx} className="text-[12px] sm:text-sm text-blue-700 flex items-start gap-1">
                                                                            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                                                            <span className="line-clamp-1">{fu.content || '메모 없음'}</span>
                                                                        </li>
                                                                    ))}
                                                                    {upcomingFollowUps.length > 5 && (
                                                                        <li className="text-[12px] text-gray-500">+{upcomingFollowUps.length - 5} 더보기</li>
                                                                    )}
                                                                </ul>
                                                            ) : (
                                                                <p className="text-[12px] sm:text-sm text-gray-500 mt-1">메모 없음</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <div className="text-xs sm:text-sm text-gray-500 mb-1">{getStageName(customer)}</div>
                                                        {/* Follow Up 날짜 및 남은 일수 표시 */}
                                                        {nearestFollowUp ? (
                                                            <div className="inline-flex items-center gap-1 text-xs sm:text-sm text-blue-700 bg-blue-100 px-2 py-1 rounded-full">
                                                                <Clock className="w-3 h-3" />
                                                                {nearestFollowUp.date} ({nearestFollowUp.daysLeft}일 후)
                                                            </div>
                                                        ) : (
                                                            <div className="inline-flex items-center gap-1 text-xs sm:text-sm text-blue-700 bg-blue-100 px-2 py-1 rounded-full">
                                                                <Clock className="w-3 h-3" />
                                                                {(customer.followUpDate || customer.followUpPlanDate || '').toString().slice(0, 10)}
                                                            </div>
                                                        )}
                                                        {/* 빠른 액션 */}
                                                        <div className="mt-2 flex items-center justify-end gap-2">
                                                            {customer.phone && (
                                                                <a href={`tel:${customer.phone}`} onClick={(e) => e.stopPropagation()} className="text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100">전화</a>
                                                            )}
                                                            <button onClick={(e) => { e.stopPropagation(); onSelectCustomer && onSelectCustomer(customer); }} className="text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100">열기</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* 캘린더 섹션 - 다가오는 할 일들 아래에 항상 표시 */}
                            <div className="mt-8 bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                                <h4 className="font-bold text-gray-800 mb-4 text-sm sm:text-base">📅 할일 캘린더</h4>
                                <div className="grid grid-cols-7 gap-2">
                                    {/* 요일 헤더 */}
                                    {['일', '월', '화', '수', '목', '금', '토'].map((day, idx) => (
                                        <div key={idx} className="text-center text-xs sm:text-sm font-semibold text-gray-600 py-2">
                                            {day}
                                        </div>
                                    ))}
                                    
                                    {/* 캘린더 날짜들 - 해당 월 전체 */}
                                    {(() => {
                                        const now = new Date();
                                        const currentYear = now.getFullYear();
                                        const currentMonth = now.getMonth();
                                        const today = new Date(currentYear, currentMonth, now.getDate());
                                        
                                        // 해당 월의 첫 날과 마지막 날
                                        const firstDay = new Date(currentYear, currentMonth, 1);
                                        const lastDay = new Date(currentYear, currentMonth + 1, 0);
                                        const daysInMonth = lastDay.getDate();
                                        const firstDayOfWeek = firstDay.getDay();
                                        
                                        const days = [];
                                        
                                        // 첫 주의 빈 칸 추가
                                        for (let i = 0; i < firstDayOfWeek; i++) {
                                            days.push(null);
                                        }
                                        
                                        // 해당 월의 모든 날짜 추가
                                        for (let day = 1; day <= daysInMonth; day++) {
                                            const date = new Date(currentYear, currentMonth, day);
                                            const yyyy = String(date.getFullYear());
                                            const mm = String(date.getMonth() + 1).padStart(2, '0');
                                            const dd = String(day).padStart(2, '0');
                                            const dateStr = `${yyyy}-${mm}-${dd}`;
                                            
                                            const todoCount = todosByDate[dateStr] || 0;
                                            const isToday = dateStr === `${String(today.getFullYear())}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                                            
                                            days.push({
                                                date: date,
                                                dateStr: dateStr,
                                                day: day,
                                                todoCount: todoCount,
                                                isToday: isToday
                                            });
                                        }
                                        
                                        return days.map((dayData, idx) => {
                                            if (dayData === null) {
                                                return <div key={`empty-${idx}`} className="aspect-square"></div>;
                                            }
                                            
                                            return (
                                                <div
                                                    key={dayData.dateStr}
                                                    onClick={() => dayData.todoCount > 0 && handleDateClick(dayData.dateStr)}
                                                    className={`relative aspect-square flex flex-col items-center justify-center rounded-lg border-2 transition-all ${
                                                        dayData.isToday
                                                            ? 'border-blue-500 bg-blue-50'
                                                            : 'border-gray-200 bg-gray-50'
                                                    } ${
                                                        dayData.todoCount > 0
                                                            ? 'hover:bg-gray-100 cursor-pointer'
                                                            : ''
                                                    }`}
                                                >
                                                    <span className={`text-xs sm:text-sm font-medium ${
                                                        dayData.isToday ? 'text-blue-700' : 'text-gray-700'
                                                    }`}>
                                                        {dayData.day}
                                                    </span>
                                                    {dayData.todoCount > 0 && (
                                                        <span className={`absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold text-white ${
                                                            dayData.isToday
                                                                ? 'bg-blue-600'
                                                                : 'bg-orange-500'
                                                        }`}>
                                                            {dayData.todoCount > 99 ? '99+' : dayData.todoCount}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 날짜별 할일 모달 */}
            {showDateModal && selectedDate && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]" onClick={() => setShowDateModal(false)}>
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl border border-white/20 flex flex-col" onClick={(e) => e.stopPropagation()}>
                        {/* 헤더 */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                                        <Calendar className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                                            {(() => {
                                                const date = new Date(selectedDate);
                                                return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
                                            })()}
                                        </h2>
                                        <p className="text-gray-600 mt-1 text-sm">할일 목록</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowDateModal(false)}
                                    className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-300"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* 콘텐츠 */}
                        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                            {loadingDateTodos ? (
                                <div className="text-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                    <p className="text-gray-600">할일을 불러오는 중...</p>
                                </div>
                            ) : dateTodos.length === 0 ? (
                                <div className="text-center py-12">
                                    <ListChecks className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-lg font-bold text-gray-600 mb-2">해당 날짜에 할일이 없습니다</h3>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {dateTodos.map((todo, idx) => (
                                            <div
                                                key={`${todo.customer.id}-${idx}`}
                                                className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                                onClick={() => {
                                                    setShowDateModal(false);
                                                    onSelectCustomer && onSelectCustomer(todo.customer);
                                                }}
                                            >
                                                <div className="flex items-center justify-between gap-3">
                                                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0">
                                                            {todo.customer.name?.charAt(0) || '고'}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <h4 className="font-bold text-gray-800 text-base sm:text-lg truncate">{todo.customer.name || '고객'}</h4>
                                                            {todo.customer.phone && (
                                                                <p className="text-gray-600 text-xs sm:text-sm truncate">{todo.customer.phone}</p>
                                                            )}
                                                            {todo.customer.company && (
                                                                <p className="text-gray-500 text-xs sm:text-sm truncate">{todo.customer.company}</p>
                                                            )}
                                                            {/* Follow Up 목록 */}
                                                            {todo.followUps && todo.followUps.length > 0 ? (
                                                                <ul className="mt-1 space-y-1">
                                                                    {todo.followUps.map((fu, fuIdx) => (
                                                                        <li key={fuIdx} className="text-[12px] sm:text-sm text-blue-700 flex items-start gap-1">
                                                                            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                                                            <span className="line-clamp-2">{fu.content || '메모 없음'}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                <p className="text-[12px] sm:text-sm text-gray-500 mt-1">메모 없음</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <div className="text-xs sm:text-sm text-gray-500 mb-1">{getStageName(todo.customer)}</div>
                                                        {/* 빠른 액션 */}
                                                        <div className="mt-2 flex items-center justify-end gap-2">
                                                            {todo.customer.phone && (
                                                                <a
                                                                    href={`tel:${todo.customer.phone}`}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className="text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                                                                >
                                                                    전화
                                                                </a>
                                                            )}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowDateModal(false);
                                                                    onSelectCustomer && onSelectCustomer(todo.customer);
                                                                }}
                                                                className="text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                                                            >
                                                                열기
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssistantModal;
